import { useCallback, useMemo, useState } from "react";
import { useInfiniteQuery } from "@tanstack/react-query";
import { api } from "../../helpers/apiClient";
import { useAppState } from "../../hooks/useAppState";

interface UserGroup {
  id: string;
  uuid: string;
  name: string;
  description: string;
  symbol?: string;
  is_owner?: boolean;
  created_at?: string;
  created_by: string;
  updated_at?: string;
  updated_by: string;
  has_conflicts?: boolean | null;
}

interface GetAllUserGroupsResponse {
  items: UserGroup[];
  total: number;
  page: number;
  pages: number;
  size: number;
}

const getUserGroups = ({ search = "", page = 1 }: { search?: string; page?: number }) =>
  api.get<GetAllUserGroupsResponse>("/groups/", { search, page, size: 50 });

const useUserGroups = () => {
  const [search, setSearch] = useState("");
  const { rbacEnabled } = useAppState();

  const query = useInfiniteQuery({
    queryKey: ["UserGroups", search],
    initialPageParam: 1,
    queryFn: ({ pageParam }) => getUserGroups({ page: pageParam, search }),
    enabled: rbacEnabled,
    gcTime: 1000 * 60 * 2, // 2 mins
    getNextPageParam: (lastPage, _allPages) => {
      if (!lastPage) return undefined;
      const totalPages = lastPage.pages;
      const currentPage = lastPage.page;
      return currentPage >= totalPages ? undefined : currentPage + 1;
    },
  });

  const groups = useMemo(() => {
    const result: UserGroup[] = [];
    query.data?.pages.forEach(page => {
      if (page?.items) {
        result.push(...page.items);
      }
    });
    return result;
  }, [query.data]);

  const getTeamNames = useCallback(
    (teamIds: string[] | null): string[] => {
      if (!teamIds || teamIds.length === 0) return ["All org"];

      const teamNames = teamIds
        .map(teamId => {
          const team = groups?.find(g => g.id.toString() === teamId.toString());
          return team?.name;
        })
        .filter((name): name is string => Boolean(name));

      return teamNames.length > 0 ? teamNames : ["All org"];
    },
    [groups]
  );

  return {
    ...query,
    search,
    setSearch,
    groups,
    total: query.data?.pages[0]?.total,
    getTeamNames,
  };
};

export default useUserGroups;
export type { UserGroup, GetAllUserGroupsResponse };
